import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  if (a === 0 && b === 0) return 1;
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a === 0 ? 1 : a;
};

const simplifyFraction = (num, den) => {
  const g = gcd(num, den);
  return {
    numerator: num / g,
    denominator: den / g,
  };
};

const fractionToLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";
  if (denominator === 1) return `${numerator}`;
  return `\\dfrac{${numerator}}{${denominator}}`;
};

/** Zamiana ułamka niewłaściwego na zapis mieszany w LaTeX-ie */
const improperToMixedLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";

  const sign = numerator < 0 ? -1 : 1;
  const absNum = Math.abs(numerator);

  // jeśli właściwy lub liczba całkowita – zostawiamy w prostszej formie
  if (absNum < denominator) {
    return fractionToLatex({ numerator, denominator });
  }

  const whole = Math.floor(absNum / denominator);
  const rem = absNum % denominator;

  if (rem === 0) {
    // sama liczba całkowita
    return `${sign * whole}`;
  }

  const fracPart = `\\dfrac{${rem}}{${denominator}}`;
  if (sign === -1) return `-${whole}\\;${fracPart}`;
  return `${whole}\\;${fracPart}`;
};

const isValidFractionObject = (f) =>
  f &&
  typeof f === "object" &&
  f.numerator !== undefined &&
  f.denominator !== undefined;

/* Porównanie dwóch ułamków zapisanych jako { numerator, denominator } */
const areFractionsEqual = (a, b) => {
  if (!isValidFractionObject(a) || !isValidFractionObject(b)) return false;

  const an = Number(a.numerator);
  const ad = Number(a.denominator);
  const bn = Number(b.numerator);
  const bd = Number(b.denominator);

  if (!isFinite(an) || !isFinite(ad) || !isFinite(bn) || !isFinite(bd)) {
    return false;
  }
  if (ad === 0 || bd === 0) return false;

  return an * bd === bn * ad;
};

/** Parsowanie tekstu na ułamek zwykły / mieszany */
const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // znak
  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    // zapis mieszany: "2 3/5"
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    // sam ułamek "3/5" albo liczba całkowita "4"
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else {
      return null;
    }
  }

  return simplifyFraction(num, den);
};

/** Parsowanie zapisu dziesiętnego (kropka lub przecinek) */
const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
};

/* ======================= Generate ======================= */
/**
 * Zadanie: Oblicz dany procent danej liczby.
 *
 * difficulty: "easy"  – procenty w wielokrotnościach 5, liczby ładne (wielokrotności 5),
 *             "hard"  – procenty spoza wielokrotności 5, liczby dowolne.
 */
export function generateTask(config = {}) {
  const { difficulty = "easy", requireIntegerResult = false } = config;

  let percent;
  let base;

  if (difficulty === "hard") {
    // procenty NIEbędące wielokrotnościami 5, np. 13%, 14%, 27% itd.
    const hardPercents = [];
    for (let p = 11; p <= 45; p++) {
      if (p % 5 !== 0) hardPercents.push(p);
    }
    percent = hardPercents[randInt(0, hardPercents.length - 1)];

    // liczba – dowolna z przedziału
    base = randInt(20, 200);
  } else {
    // easy: wielokrotności 5 (np. 5%, 10%, 15%, ... 80%)
    const easyPercents = [];
    for (let p = 5; p <= 80; p += 5) {
      easyPercents.push(p);
    }
    percent = easyPercents[randInt(0, easyPercents.length - 1)];

    // W przygotowaniu dobieramy wielokrotność 20, aby wynik był całkowity.
    const niceBases = [];
    for (let b = 20; b <= 200; b++) {
      if (b % (requireIntegerResult ? 20 : 5) === 0) niceBases.push(b);
    }
    base = niceBases[randInt(0, niceBases.length - 1)];
  }

  // wynik jako ułamek: (percent/100) * base
  const rawNumerator = percent * base;
  const rawDenominator = 100;
  const resultFraction = simplifyFraction(rawNumerator, rawDenominator);

  return {
    type: "PercentOfNumberTask",
    data: {
      difficulty,
      percent,
      base,
      resultFraction,
    },
  };
}

/* ======================= Render task ======================= */

export function renderTask({ data }) {
  const { percent, base } = data;

  return (
    <p>
      Oblicz{" "}
      <InlineMath math={`${percent}\\%`} />{" "}
      z{" "}
      <InlineMath math={`${base}`} />.
    </p>
  );
}

/* ======================= Input (FractionInput) ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder="np. 3/4, 1 1/2, 0.75"
        label="" // dalej bez "a ="
      />
    </div>
  );
}


/* ======================= Check ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.resultFraction;
  const expectedValue = expected.numerator / expected.denominator;

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return {
      isCorrect: false,
      expected,
      actual: null,
      comment: null,
    };
  }

  const raw = String(userAnswer.raw).trim();

  // 1) Najpierw próba jako ułamek
  const parsedFrac = parseFractionFromString(raw);
  if (parsedFrac && areFractionsEqual(expected, parsedFrac)) {
    return {
      isCorrect: true,
      expected,
      actual: parsedFrac,
      comment: null,
    };
  }

  // 2) Próba jako zapis dziesiętny
  const decimalVal = parseDecimalFromString(raw);
  if (decimalVal !== null) {
    const approxNum = Math.round(decimalVal * 100);
    const approxDen = 100;
    const approxFrac = simplifyFraction(approxNum, approxDen);

    const sameFraction = areFractionsEqual(expected, approxFrac);
    const closeEnough = Math.abs(decimalVal - expectedValue) <= 0.005;

    const isCorrect = sameFraction || closeEnough;

    return {
      isCorrect,
      expected,
      actual: approxFrac,
      comment: null,
    };
  }

  // 3) Nic nie pasuje – odpowiedź błędna
  return {
    isCorrect: false,
    expected,
    actual: null,
    comment: null,
  };
}

/* ======================= Answer & User Answer ======================= */

export function renderAnswer({ data }) {
  const { resultFraction } = data;
  const latex = improperToMixedLatex(resultFraction);

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={latex} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.raw ||
    !String(userAnswer.raw).trim()
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const raw = String(userAnswer.raw).trim();

  // najpierw spróbuj jako ułamek
  const parsedFrac = parseFractionFromString(raw);
  if (parsedFrac) {
    const latex = fractionToLatex(parsedFrac);
    return (
      <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
        <InlineMath math={latex} />
      </div>
    );
  }

  // potem jako zapis dziesiętny
  const decimalVal = parseDecimalFromString(raw);
  if (decimalVal !== null) {
    const normalized = raw.replace(",", ".");
    return (
      <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
        <InlineMath math={normalized} />
      </div>
    );
  }

  // fallback – traktujemy zawartość jako TeX (np. ktoś wpisze coś własnego)
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={raw} />
    </div>
  );
}


/* ======================= Explanation ======================= */

export function renderExplanation({ data }) {
  const { percent, base, resultFraction } = data;

  const step1 = `${percent}\\% = \\dfrac{${percent}}{100}`;
  const step2 = `\\dfrac{${percent}}{100} \\cdot ${base}`;
  const resultLatex = improperToMixedLatex(resultFraction);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Zapisz procent w postaci ułamka.
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2:</strong> Pomnóż ten ułamek przez daną liczbę.
      </p>
      <BlockMath math={step2} />

      <p>
        <strong>Krok 3:</strong> Uprość otrzymany ułamek i (jeśli trzeba)
        zapisz go jako ułamek mieszany.
      </p>
      <BlockMath math={resultLatex} />
    </div>
  );
}
